import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Textarea,
} from "@chakra-ui/react";

/**
 * DocumentMetadataEditor - Modal for editing row metadata
 * Only users with the row's adminBy role can edit metadata
 *
 * Editable fields:
 * - segmentType
 * - segmentName
 * - fieldId
 * - headingType
 * - editableBy (comma-separated roles)
 * - adminBy (comma-separated roles)
 */
export default function DocumentMetadataEditor({
  isOpen,
  onClose,
  rowData,
  userRoles = [],
  onSave,
}) {
  const toast = useToast();
  const [metadata, setMetadata] = useState({
    segmentType: "",
    segmentName: "",
    fieldId: "",
    headingType: "",
    editableBy: "",
    adminBy: "",
  });

  // Check if user has admin permission for this row
  const hasAdminPermission = () => {
    if (!rowData?.adminBy || !Array.isArray(rowData.adminBy)) {
      return false; // No adminBy means locked
    }

    return rowData.adminBy.some(adminRole => {
      return userRoles.some(userRole => {
        const userRoleCode = userRole.role || userRole;
        return adminRole.toLowerCase() === userRoleCode.toLowerCase();
      });
    });
  };

  const canEdit = hasAdminPermission();

  // Initialize metadata from rowData
  useEffect(() => {
    if (rowData) {
      setMetadata({
        segmentType: rowData.segmentType || "",
        segmentName: rowData.segmentName || "",
        fieldId: rowData.fieldId || "",
        headingType: rowData.headingType || "",
        editableBy: Array.isArray(rowData.editableBy)
          ? rowData.editableBy.join(", ")
          : (rowData.editableBy || ""),
        adminBy: Array.isArray(rowData.adminBy)
          ? rowData.adminBy.join(", ")
          : (rowData.adminBy || ""),
      });
    }
  }, [rowData]);

  const handleChange = (field, value) => {
    setMetadata(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!canEdit) {
      toast({
        title: "Permission Denied",
        description: "You don't have admin permission for this row",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Convert comma-separated strings back to arrays
    const updatedMetadata = {
      ...metadata,
      editableBy: metadata.editableBy
        .split(",")
        .map(s => s.trim())
        .filter(Boolean),
      adminBy: metadata.adminBy
        .split(",")
        .map(s => s.trim())
        .filter(Boolean),
    };

    onSave(updatedMetadata);

    toast({
      title: "Metadata Updated",
      description: "Row metadata has been successfully updated",
      status: "success",
      duration: 2000,
      isClosable: true,
    });

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Row Metadata</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {!canEdit && (
            <Text color="red.500" mb={4} fontSize="sm" fontWeight="bold">
              ⚠️ You don't have admin permission for this row. Viewing only.
            </Text>
          )}

          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Segment Type</FormLabel>
              <Input
                size="sm"
                value={metadata.segmentType}
                onChange={(e) => handleChange("segmentType", e.target.value)}
                isDisabled={!canEdit}
                placeholder="e.g., header, content, footer"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Segment Name</FormLabel>
              <Input
                size="sm"
                value={metadata.segmentName}
                onChange={(e) => handleChange("segmentName", e.target.value)}
                isDisabled={!canEdit}
                placeholder="e.g., Introduction, Summary"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Field ID</FormLabel>
              <Input
                size="sm"
                value={metadata.fieldId}
                onChange={(e) => handleChange("fieldId", e.target.value)}
                isDisabled={!canEdit}
                placeholder="e.g., field_001"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Heading Type</FormLabel>
              <Input
                size="sm"
                value={metadata.headingType}
                onChange={(e) => handleChange("headingType", e.target.value)}
                isDisabled={!canEdit}
                placeholder="e.g., Section Title, Clause"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Editable By (comma-separated roles)</FormLabel>
              <Textarea
                size="sm"
                value={metadata.editableBy}
                onChange={(e) => handleChange("editableBy", e.target.value)}
                isDisabled={!canEdit}
                placeholder="e.g., broker, underwriter, admin"
                rows={2}
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Users with these roles can edit the content of this row
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Admin By (comma-separated roles)</FormLabel>
              <Textarea
                size="sm"
                value={metadata.adminBy}
                onChange={(e) => handleChange("adminBy", e.target.value)}
                isDisabled={!canEdit}
                placeholder="e.g., admin, manager"
                rows={2}
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Users with these roles can edit the metadata and permissions of this row
              </Text>
            </FormControl>

            <Text fontSize="xs" color="gray.500" fontStyle="italic">
              Row ID: {rowData?.id || "N/A"}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            size="sm"
            isDisabled={!canEdit}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
